import { formatMoney } from './final-settlement-parts';

// Pure presentational pieces of the sell-side Final Invoice (Launchpad parity).
// Data fetching + composition lives in `FinalInvoiceSellForm`; these only render.

export function FinalDownpaymentBanner(props: {
  readonly allocated: number;
  readonly remaining: number;
  readonly currency: string | null;
}) {
  return (
    <p
      className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
      data-testid="final-invoice-downpayment-banner"
    >
      <span className="font-semibold">
        Downpayment of {formatMoney(props.allocated, props.currency)} already applied to this
        invoice.
      </span>{' '}
      The payment schedule below covers the remaining{' '}
      <span className="font-semibold">{formatMoney(props.remaining, props.currency)}</span> balance.
    </p>
  );
}

export function FinalSettlementSummary(props: {
  readonly gross: number;
  readonly allocated: number;
  readonly provisional: number;
  readonly net: number;
  readonly currency: string | null;
  readonly blShipmentCount: number | null;
  readonly totalShipments: number | null;
}) {
  // Launchpad shows the proportional-share caveat only when the order spans
  // more than one shipment.
  const shipmentNote =
    props.totalShipments !== null && props.totalShipments > 1 && props.blShipmentCount !== null
      ? ` (${String(props.blShipmentCount)}/${String(props.totalShipments)} shipments)`
      : '';
  const amountDue = Math.max(props.net, 0);
  const overpayment = Math.max(-props.net, 0);
  return (
    <dl
      className="bg-muted/40 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md p-3 text-sm"
      data-testid="final-settlement-summary"
    >
      <dt className="text-muted-foreground text-xs">Final Cargo Value</dt>
      <dd>{formatMoney(props.gross, props.currency)}</dd>
      {props.allocated > 0 && (
        <>
          <dt className="text-muted-foreground text-xs">Less Downpayment Received{shipmentNote}</dt>
          <dd>−{formatMoney(props.allocated, props.currency)}</dd>
        </>
      )}
      {props.provisional > 0 && (
        <>
          <dt className="text-muted-foreground text-xs">Less Provisional Payments Received</dt>
          <dd>−{formatMoney(props.provisional, props.currency)}</dd>
        </>
      )}
      <dt className="font-semibold">Amount Due on Final Invoice</dt>
      <dd
        className={overpayment > 0 ? 'text-amber-600 font-semibold' : 'font-semibold'}
        data-testid="final-settlement-net"
      >
        {formatMoney(amountDue, props.currency)}
      </dd>
      {overpayment > 0 && (
        <>
          <dt className="text-amber-600 font-medium">Overpayment</dt>
          <dd className="text-amber-600 font-medium">{formatMoney(overpayment, props.currency)}</dd>
        </>
      )}
    </dl>
  );
}
